import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { Menu } from 'primeng/menu';
import { Tooltip } from 'primeng/tooltip';
import type { MenuItem } from 'primeng/api';
import { SvgIconComponent } from '../../../shared/svg-icon/svg-icon.component';
import { FileItem } from '../../../models';
import { getFileIcon } from '../file-type.utils';

@Component({
  selector: 'app-file-grid-view',
  standalone: true,
  imports: [CommonModule, FormsModule, CheckboxModule, Menu, Tooltip, SvgIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './file-grid-view.component.html',
  styleUrl: './file-grid-view.component.scss',
})
export class FileGridView {
  @Input() folders: FileItem[] = [];
  @Input() filesList: FileItem[] = [];
  @Input() selectedFiles: FileItem[] = [];
  @Output() selectedFilesChange = new EventEmitter<FileItem[]>();
  readonly highlightedFileName = input<string | null>(null);

  @Output() navigateToFolder = new EventEmitter<FileItem>();
  @Output() deleteSelected = new EventEmitter<void>();
  @Output() previewFile = new EventEmitter<FileItem>(); 
  @Output() downloadFile = new EventEmitter<FileItem>();
  @Output() deleteItem = new EventEmitter<FileItem>();
  @Output() fileDragStart = new EventEmitter<{ event: DragEvent; file: FileItem }>();
  @Output() showLinks = new EventEmitter<FileItem>();

  @ViewChild('gridMenu') gridMenu!: Menu;

  activeItem: FileItem | null = null;
  menuItems: MenuItem[] = [];

  private readonly host = inject(ElementRef<HTMLElement>);

  constructor() {
    effect(() => {
      const name = this.highlightedFileName();
      if (!name) return;
      requestAnimationFrame(() => {
        const card = this.host.nativeElement.querySelector(`[data-filename="${CSS.escape(name)}"]`);
        card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  getFileIcon(extension: string): string {
    return getFileIcon(extension);
  }

  onSelectedFilesChange(value: FileItem[]): void {
    this.selectedFiles = value;
    this.selectedFilesChange.emit(value);
  }

  openMenu(event: MouseEvent, item: FileItem): void {
    event.stopPropagation();
    this.activeItem = item;
    this.menuItems = [
      ...(item.type === 'file'
        ? [
            {
              label: 'Megtekintés',
              icon: 'pi pi-eye',
              disabled: item.isScanning,
              command: () => this.activeItem && this.previewFile.emit(this.activeItem),
            },
            {
              label: 'Letöltés',
              icon: 'pi pi-download',
              disabled: item.isScanning,
              command: () => this.activeItem && this.downloadFile.emit(this.activeItem),
            },
          ]
        : []),
      {
        label: 'Törlés',
        icon: 'pi pi-trash',
        disabled: !!item.links || item.isScanning,
        tooltip: item.isScanning ? 'Vírusellenőrzés alatt' : (item.links ? 'Nem törölhető: a fájl linkelt' : undefined),
        command: () => this.activeItem && this.deleteItem.emit(this.activeItem),
      },
    ];
    this.gridMenu.toggle(event);
  }
}
